import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent, setUserProperties } from "firebase/analytics";
import {
  fetchAndActivate,
  getBoolean,
  getNumber,
  getRemoteConfig,
} from "firebase/remote-config";
import {
  ENV,
  FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID,
  VERSION,
} from "gatsby-env-variables";
import { isViewAsTab } from "@helpers/viewAs/viewAsSession";
const firebaseConfig = {
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: FIREBASE_STORAGE_BUCKET,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  appId: FIREBASE_APP_ID,
  measurementId: FIREBASE_MEASUREMENT_ID,
};

const REMOTE_CONFIG_EXPECTED_ERROR_CODES = [
  "installations/app-offline",
  "remoteconfig/storage-open",
  "remoteconfig/storage-get",
  "remoteconfig/storage-set",
  "remoteconfig/indexed-db-unavailable",
] as const;

const PROD_FETCH_INTERVAL_MS = 60 * 60 * 1000;

const REMOTE_CONFIG_OBJECT_STORE_NAME = "app_namespace_store";
const REMOTE_CONFIG_OBJECT_STORE_ERROR = "not a known object store name";

const isExpectedRemoteConfigError = (error: unknown) => {
  const errorMessage =
    typeof (error as { message?: unknown } | undefined)?.message === "string"
      ? ((error as { message?: string }).message as string)
      : "";

  const errorCode =
    typeof (error as { code?: unknown } | undefined)?.code === "string"
      ? ((error as { code?: string }).code as string)
      : "";

  const isExpectedErrorCode = REMOTE_CONFIG_EXPECTED_ERROR_CODES.some(
    (code) => errorCode.includes(code) || errorMessage.includes(code)
  );

  return (
    isExpectedErrorCode ||
    (errorMessage.includes(REMOTE_CONFIG_OBJECT_STORE_NAME) &&
      errorMessage.toLowerCase().includes(REMOTE_CONFIG_OBJECT_STORE_ERROR))
  );
};

// Initialize Firebase
const initFirebase = async () => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return null;
  }

  try {
    const app = initializeApp(firebaseConfig);

    if (!isViewAsTab()) {
      try {
        const analytics = getAnalytics(app);
        logEvent(analytics, "notification_received");
        setUserProperties(analytics, { web_app_version: VERSION });
      } catch {
        // Analytics failures (e.g. privacy blockers on iOS Safari) are non-critical
      }
    }

    const remoteConfig = getRemoteConfig(app);
    // Off the live site every load fetches, so a console toggle shows up at
    // once instead of being masked by the SDK's cache for the next hour.
    remoteConfig.settings.minimumFetchIntervalMillis =
      ENV === "prod" ? PROD_FETCH_INTERVAL_MS : 0;
    await fetchAndActivate(remoteConfig);

    return {
      FF_LIVE_CHAT: getBoolean(remoteConfig, "FF_LIVE_CHAT"),
      USD_GBP_EXCHANGE_RATE: getNumber(remoteConfig, "USD_GBP_EXCHANGE_RATE"),
      GBP_USD_EXCHANGE_RATE: getNumber(remoteConfig, "GBP_USD_EXCHANGE_RATE"),
      FF_TURNSTILE_WEB_ENABLED: getBoolean(
        remoteConfig,
        "FF_TURNSTILE_WEB_ENABLED"
      ),
      FF_PLAID_BANK_ACCOUNT_CONNECTION: getBoolean(
        remoteConfig,
        "FF_PLAID_BANK_ACCOUNT_CONNECTION"
      ),
      FF_EIS_FUND_V2: getBoolean(remoteConfig, "FF_EIS_FUND_V2"),
      FF_FUND_TENORS_EXPLAINER: getBoolean(
        remoteConfig,
        "FF_FUND_TENORS_EXPLAINER"
      ),
      FF_NEW_DEAL_CARD: getBoolean(remoteConfig, "FF_NEW_DEAL_CARD"),
      FF_ENTITY_TAX_RESIDENCIES: getBoolean(
        remoteConfig,
        "FF_ENTITY_TAX_RESIDENCIES"
      ),
      FF_PORTFOLIO_STATEMENT_BY_DATE: getBoolean(
        remoteConfig,
        "FF_PORTFOLIO_STATEMENT_BY_DATE"
      ),
      FF_DEAL_DETAILS_V3: getBoolean(remoteConfig, "FF_DEAL_DETAILS_V3"),
      FF_ISA_REALLOCATIONS: getBoolean(remoteConfig, "FF_ISA_REALLOCATIONS"),
      FF_HOMEPAGE: getBoolean(remoteConfig, "FF_HOMEPAGE"),
      FF_APP_V3: getBoolean(remoteConfig, "FF_APP_V3"),
      FF_DEAL_ANCHORED_ONBOARDING: getBoolean(remoteConfig, "FF_DEAL_ANCHORED_ONBOARDING"),
      FF_UPCOMING_EARNINGS: getBoolean(remoteConfig, "FF_UPCOMING_EARNINGS"),
      FF_DEFAULT_BANK_ACCOUNTS: getBoolean(
        remoteConfig,
        "FF_DEFAULT_BANK_ACCOUNTS"
      ),
    };
  } catch (error) {
    if (isExpectedRemoteConfigError(error)) {
      return null;
    }

    console.error("Error fetching Remote Config:", error);
    return null;
  }
};

export default initFirebase;
